import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { AccountPage } from "./pages/AccountPage";
import { ActivityPage } from "./pages/ActivityPage";
import { AdminPage } from "./pages/AdminPage";
import { AppShell } from "./components/AppShell";
import { ChatPage, ChatsIndexPage } from "./pages/ChatPage";
import { CreatePage } from "./pages/CreatePage";
import { LibraryPage } from "./pages/LibraryPage";
import { LoginPage } from "./pages/LoginPage";

export type CreateKind = "image" | "video" | "music" | "speech" | "enhance";
const KINDS: CreateKind[] = ["image", "video", "music", "speech", "enhance"];

/** `?asset=<id>` opens the viewer over any page, so a result can be shared by link and closed with Back. */
const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: AppShell,
  validateSearch: (search: Record<string, unknown>): { asset?: string } => ({
    asset: typeof search.asset === "string" ? search.asset : undefined,
  }),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/create", search: { kind: "video" } });
  },
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

export const createRouteDef = createRoute({
  getParentRoute: () => rootRoute,
  path: "/create",
  component: CreatePage,
  validateSearch: (search: Record<string, unknown>): { kind: CreateKind } => ({
    kind: KINDS.includes(search.kind as CreateKind) ? (search.kind as CreateKind) : "video",
  }),
});

export const chatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat",
  component: ChatsIndexPage,
});

export const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat/$chatId",
  component: ChatPage,
});

export type LibraryFilter = "all" | "image" | "video" | "audio" | "document" | "favorites";
const FILTERS: LibraryFilter[] = ["all", "image", "video", "audio", "document", "favorites"];

export const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library",
  component: LibraryPage,
  validateSearch: (search: Record<string, unknown>): { filter: LibraryFilter; q?: string } => ({
    filter: FILTERS.includes(search.filter as LibraryFilter) ? (search.filter as LibraryFilter) : "all",
    q: typeof search.q === "string" && search.q ? search.q : undefined,
  }),
});

export const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity",
  component: ActivityPage,
});

export const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  createRouteDef,
  chatsRoute,
  chatRoute,
  libraryRoute,
  activityRoute,
  accountRoute,
  adminRoute,
]);

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined as unknown as QueryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
